import {
  ActionWithPayload,
  NicheFilesFilter,
  PaginatedResponse,
  ResponseError,
  WithCallback,
} from "@shared/interfaces";
import { call, delay, put, takeEvery, takeLatest } from "redux-saga/effects";
import { showNotification, startLoading, stopLoading } from "@shared/store/actions";
import {
  NicheFilesCancelSubscriptionPayload,
  NicheFilesCountyPriceResult,
  NicheFilesCreateSubscriptionPayload,
  NicheFilesFetchCountyPricePayload,
  NicheFilesGetSubscriptionPayload,
  NicheFilesGetSubscriptionsPayload,
  NicheFilesRenewSubscriptionPayload,
} from "@containers/NicheFiles/interface";
import { NicheFile, NicheFilesSubscription, SubscriptionStatus } from "@shared/models";
import { showHttpErrorNotification } from "@shared/utils";
import { download } from "@shared/utils/request";

import api from "../api";

import { actions } from "./";

function* fetchCountyPricesSaga({ payload }: ActionWithPayload<NicheFilesFetchCountyPricePayload>) {
  try {
    const response: NicheFilesCountyPriceResult[] = yield call(api.getNicheFilesPrice, payload);

    yield put(actions.fetchNicheFilesCountyPrice.success(response));
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError, "Failed to load price. Please try again later"));
  }
}

function* getNicheFilesSubscriptionsSaga({
  payload,
}: ActionWithPayload<WithCallback<NicheFilesGetSubscriptionsPayload, NicheFilesSubscription[]>>) {
  const { do_not_show_loading, account_id, callback } = payload;

  try {
    if (!do_not_show_loading) yield put(startLoading());

    const subscriptions: NicheFilesSubscription[] = yield call(api.getNicheFilesSubscriptions, { account_id });

    callback?.(subscriptions);

    yield put(actions.getNicheFilesSubscriptions.success(subscriptions));
  } catch (error) {
    if (!do_not_show_loading) {
      yield put(
        showHttpErrorNotification(error as ResponseError, "Failed to load subscriptions. Please try again later."),
      );
    }
  } finally {
    if (!do_not_show_loading) yield put(stopLoading());
  }
}

function* getNicheFilesSubscriptionSaga({ payload }: ActionWithPayload<NicheFilesGetSubscriptionPayload>) {
  const { do_not_show_loading, account_id, id } = payload;

  try {
    if (!do_not_show_loading) yield put(startLoading());

    const response: NicheFilesSubscription = yield call(api.getNicheFilesSubscription, { id, account_id });
    const subscription = response?.id ? response : null;

    yield put(actions.getNicheFilesSubscription.success(subscription));
  } catch (error) {
    if (!do_not_show_loading) {
      yield put(
        showHttpErrorNotification(error as ResponseError, "Failed to load subscription. Please try again later."),
      );
    }
  } finally {
    if (!do_not_show_loading) yield put(stopLoading());
  }
}

function* createSubscriptionSaga({
  payload: { body, callback, onFailed },
}: ActionWithPayload<{
  body: NicheFilesCreateSubscriptionPayload;
  callback: () => void;
  onFailed: () => void;
}>) {
  try {
    yield put(startLoading());

    let response: NicheFilesSubscription = yield call(
      body.id ? api.updateNicheFilesSubscription : api.createNicheFilesSubscription,
      body,
    );

    if (body.id && (response.status === SubscriptionStatus.CANCELLED || response.cancelled_at)) {
      response = yield call(api.renewNicheFilesSubscription, { id: body.id, account_id: body.account_id });
    }

    yield put(actions.getNicheFilesSubscription.success(response));

    callback();
  } catch (err: unknown) {
    const error = err as ResponseError;

    yield put(
      showHttpErrorNotification(
        error as ResponseError,
        (error as ResponseError).message || "Failed to save subscription. Please try again later.",
      ),
    );

    onFailed?.();
  }
}

function* cancelSubscriptionSaga({ payload }: ActionWithPayload<NicheFilesCancelSubscriptionPayload>) {
  try {
    yield put(startLoading());
    const response: NicheFilesSubscription = yield call(api.cancelNicheFilesSubscription, payload);

    yield put(actions.getNicheFilesSubscription.success(response));
  } catch (error) {
    yield put(
      showHttpErrorNotification(error as ResponseError, "Failed to cancel subscription. Please try again later."),
    );
  } finally {
    yield put(stopLoading());
  }
}

function* renewSubscriptionSaga({ payload }: ActionWithPayload<NicheFilesRenewSubscriptionPayload>) {
  try {
    yield put(startLoading());
    const response: NicheFilesSubscription = yield call(api.renewNicheFilesSubscription, payload);

    yield put(actions.getNicheFilesSubscription.success(response));

    yield put(
      showNotification({
        appearance: "success",
        message: "Your subscription has been successfully reactivated.",
      }),
    );
  } catch (error) {
    yield put(
      showHttpErrorNotification(error as ResponseError, "Failed to renew subscription. Please try again later."),
    );
  } finally {
    yield put(stopLoading());
  }
}

function* getNicheFilesSaga({ payload }: ActionWithPayload<NicheFilesFilter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<NicheFile> = yield call(api.getNicheFiles, payload);
    yield put(
      actions.getNicheFiles.success({
        ...response,
        clear: !payload.page || payload.page === 1,
      }),
    );
  } catch (error) {
    yield put(actions.getNicheFiles.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* getNicheFileSignedUrlSaga({ payload }: ReturnType<typeof actions.downloadNicheFile>) {
  const { id, callback } = payload;
  try {
    yield put(startLoading());
    const { url }: { url: string } = yield call(api.getNicheFileSignedUrl, id);

    yield call(download, url);

    yield put(
      showNotification({
        message: "The file was successfully downloaded",
        appearance: "success",
      }),
    );
    if (callback) {
      // Fixing Safari issue. It throws error when it starts downloading file
      // and sends request to the server at the same time
      setTimeout(() => {
        callback();
      }, 100);
    }
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError, "Can't download file"));
  } finally {
    yield put(stopLoading());
  }
}

function* getNicheFileSaga({ payload }: ReturnType<typeof actions.getNicheFile.request>) {
  try {
    if (!payload.hideLoader) {
      yield put(startLoading());
    }
    const response: NicheFile = yield call(api.getNicheFile, payload.id);
    yield put(actions.getNicheFile.success(response));
    if (payload.callback) {
      payload.callback();
    }
  } catch (error) {
    yield put(actions.getNicheFile.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    if (!payload.hideLoader) {
      yield put(stopLoading());
    }
  }
}

function* deleteNicheFileSaga({ payload }: ReturnType<typeof actions.deleteNicheFile.request>) {
  try {
    if (!payload.hideLoader) {
      yield put(startLoading());
    }
    yield call(api.deleteNicheFile, payload.id);
    yield put(actions.deleteNicheFile.success({ id: payload.id }));
  } catch (error) {
    yield put(actions.deleteNicheFile.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    if (!payload.hideLoader) {
      yield put(stopLoading());
    }
  }
}

function* updateNicheFileStatusSaga({ payload }: ReturnType<typeof actions.updateNicheFileStatus.request>) {
  try {
    if (!payload.hideLoader) {
      yield put(startLoading());
    }
    const response: NicheFile = yield call(api.updateNicheFileStatus, payload);
    yield put(actions.updateNicheFileStatus.success(response));
  } catch (error) {
    yield put(actions.updateNicheFileStatus.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    if (!payload.hideLoader) {
      yield put(stopLoading());
    }
  }
}

function* waitTillSubscriptionActivatedSaga({ payload }: ActionWithPayload<NicheFilesGetSubscriptionPayload>) {
  if (!payload.wait_for_active) {
    return;
  }

  let attempt = 0;

  try {
    yield put(startLoading());

    do {
      yield delay(1000);

      const subscription: NicheFilesSubscription = yield call(api.getNicheFilesSubscription, {
        id: payload.id,
        account_id: payload.account_id,
      });

      if (subscription && subscription.status === SubscriptionStatus.ACTIVE) {
        yield put(actions.getNicheFilesSubscription.success(subscription));
        break;
      }
    } while (++attempt <= 30);
  } catch (error) {
    yield put(actions.getNicheFilesSubscription.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* createCheckoutLinkSaga({ payload }: ReturnType<typeof actions.createNicheFilesCheckoutLink>) {
  try {
    yield put(startLoading());
    const sub: NicheFilesSubscription = yield call(api.createCheckoutLink, {
      id: payload.id,
      account_id: payload.account_id,
    });

    if (payload.callback) {
      payload.callback(sub);
    }
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* nicheFilesSagas() {
  yield takeLatest(actions.fetchNicheFilesCountyPrice.request, fetchCountyPricesSaga);
  yield takeLatest(actions.getNicheFilesSubscriptions.request, getNicheFilesSubscriptionsSaga);
  yield takeLatest(actions.getNicheFilesSubscription.request, getNicheFilesSubscriptionSaga);
  yield takeLatest(actions.getNicheFilesSubscription.request, waitTillSubscriptionActivatedSaga);
  yield takeLatest(actions.createNicheFilesSubscription.request, createSubscriptionSaga);
  yield takeLatest(actions.cancelNicheFilesSubscription.request, cancelSubscriptionSaga);
  yield takeLatest(actions.renewNicheFilesSubscription.request, renewSubscriptionSaga);
  yield takeLatest(actions.getNicheFiles.request, getNicheFilesSaga);
  yield takeLatest(actions.getNicheFile.request, getNicheFileSaga);
  yield takeEvery(actions.downloadNicheFile, getNicheFileSignedUrlSaga);
  yield takeLatest(actions.deleteNicheFile.request, deleteNicheFileSaga);
  yield takeLatest(actions.updateNicheFileStatus.request, updateNicheFileStatusSaga);
  yield takeLatest(actions.createNicheFilesCheckoutLink, createCheckoutLinkSaga);
}

export default nicheFilesSagas;
